type Completion = { pending: boolean; skipped: boolean; lastStep: number };
const memory = new Map<number, Completion>();
const syncing = new Set<number>();
const key = (userId: number) => `admoji-tour-completed:${userId}`;
function read(userId: number): Completion | undefined {
  try {
    const stored = JSON.parse(localStorage.getItem(key(userId)) || 'null');
    if (stored && typeof stored.pending === 'boolean') return stored as Completion;
  } catch { /* session memory also supports browsers that block storage */ }
  return memory.get(userId);
}
function write(userId: number, completion: Completion) {
  memory.set(userId, completion);
  try { localStorage.setItem(key(userId), JSON.stringify(completion)); } catch { /* session fallback */ }
}
export function isTourCompleted(userId: number | undefined, serverCompleted?: boolean): boolean {
  return serverCompleted === true || (userId != null && read(userId) != null);
}
export function markTourCompleted(userId: number, skipped: boolean, lastStep: number) {
  write(userId, { pending: true, skipped, lastStep });
}
/** A failed/offline write remains queued across reloads; later visits retry it. */
export async function syncTourCompletion(userId: number, serverCompleted?: boolean): Promise<void> {
  const completion = read(userId);
  if (serverCompleted) {
    write(userId, { pending: false, skipped: completion?.skipped ?? false, lastStep: completion?.lastStep ?? 0 });
    return;
  }
  if (!completion?.pending || syncing.has(userId)) return;
  syncing.add(userId);
  try {
    const response = await fetch('/api/tour/complete', {
      method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ skipped: completion.skipped, lastStep: completion.lastStep }),
    });
    if (response.ok) write(userId, { ...completion, pending: false });
  } catch { /* keep pending until the next visit or reconnect */ }
  finally { syncing.delete(userId); }
}
